/*
RISKS-SORT
Choose sorting order for the risks
*/

import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useEffect } from "react"
import riskStore from "../model/risk.store"
import { t } from "i18next"
import ListButton from "@/components/list-button"
import {
  faSortAlphaAsc,
  faSortAmountDown,
  faSortNumericDesc,
  faHeartPulse,
  faHelmetSafety,
  faClock,
  faClipboardList
} from "@fortawesome/free-solid-svg-icons"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"
import RisksToggleAll from "./RisksToggleAll"
import unitStore from "../../unit/model/unit.store"
interface Props {
  hideFirstBorder?: boolean
  isUnitView?: boolean
}

export default function RisksSort({ hideFirstBorder = false, isUnitView = false }: Props) {
  const dispatch = useAppDispatch()

  const units = useAppSelector(unitStore.selectors.list)
  const organization = useAppSelector(organizationStore.selectors.active)
  const sort = useAppSelector(riskStore.selectors.sort)
  const filter = useAppSelector(riskStore.selectors.filter)
  const riskFamilies = useAppSelector(riskStore.selectors.listSelectedByUnits) as any[]

  const firstAttrName = organization.riskAttributes[0]

  // Check if there are RPS or PHY risks
  const hasRpsRisks = riskFamilies.some((family) => family.Risks?.some((risk: any) => risk.rps))
  const hasPhyRisks = riskFamilies.some((family) => family.Risks?.some((risk: any) => !risk.rps))

  // Check if any unit has a size value
  const hasUnitSize = units.some((unit) => unit.size != null)

  // Check if there are incomplete risks (missing values in attributes)
  const isRiskIncomplete = (risk: any): boolean => {
    return Object.values(risk.attributes ?? {}).some((value: any) => !value || value.trim?.() === "")
  }
  const hasIncompleteRisks = riskFamilies.some((family) => family.Risks?.some((risk: any) => isRiskIncomplete(risk)))

  const sortItems = [
    ...(hasUnitSize
      ? [
          {
            key: "size",
            name: t("units_size"),
            asc: false,
            keySecondary: "riskMax.valueEffective",
            ascSecondary: false,
            icon: faSortNumericDesc
          }
        ]
      : []),
    {
      key: "riskMax.valueEffectiveIndex",
      name: t("risk_item_rr"),
      asc: false,
      keySecondary: "size",
      ascSecondary: false,
      icon: faSortAmountDown
    },
    {
      key: "name",
      name: firstAttrName ? "Nom" : "",
      asc: true,
      icon: faSortAlphaAsc
    },
    {
      key: "dateValidated",
      name: "plus récent",
      asc: false,
      icon: faClock
    }
  ].map((item) => ({
    ...item,
    name: `Trier par ${item.name.toLowerCase()}`
  }))

  useEffect(() => {
    if (!getItem()) {
      dispatch(riskStore.actions.sort(sortItems[0]))
    }
  }, [riskFamilies.length, dispatch])

  function getItem() {
    return sortItems.find((x) => x.key === sort.key)
  }

  const filterItems = [
    ...(hasRpsRisks || filter?.rps != null
      ? [
          {
            key: "rps-true",
            name: t("rps"),
            icon: faHeartPulse,
            value: true
          }
        ]
      : []),
    ...(hasPhyRisks || filter?.rps != null
      ? [
          {
            key: "rps-false",
            name: t("PHY"),
            icon: faHelmetSafety,
            value: false
          }
        ]
      : []),
    ...(!isUnitView && (hasIncompleteRisks || filter?.incomplete)
      ? [
          {
            key: "incomplete-true",
            name: "Fiches à compléter",
            icon: faClipboardList,
            value: true
          }
        ]
      : [])
  ]

  return (
    <div className="flex flex-1 grey-b m-auto">
      <div style={hideFirstBorder ? {} : { borderLeft: "1px solid #8C8C8C" }} className="flex pl-3 ml-3">
        {sortItems.map((item) => (
          <ListButton
            isPrimary={sort.key === item.key}
            icon={item.icon}
            key={item.key}
            text={item.name}
            onClick={() => dispatch(riskStore.actions.sort(item))}
          />
        ))}
      </div>

      {(hasRpsRisks && hasPhyRisks) || hasIncompleteRisks || filter?.rps != null || filter?.incomplete ? (
        <div style={{ borderLeft: "1px solid #8C8C8C" }} className="flex pl-3 ml-3">
          {filterItems.map((item) => (
            <ListButton
              isPrimary={item.key.includes("rps") ? filter?.rps === item.value : filter?.incomplete === item.value}
              icon={item.icon}
              key={item.key}
              text={`Filtrer par ${item.name.toLowerCase()}`}
              onClick={() => {
                if (item.key.includes("rps")) {
                  dispatch(
                    riskStore.actions.setFilter(
                      filter?.rps === item.value ? { ...filter, rps: null } : { ...filter, rps: item.value }
                    )
                  )
                } else {
                  dispatch(
                    riskStore.actions.setFilter(
                      filter?.incomplete === item.value
                        ? { ...filter, incomplete: null }
                        : { ...filter, incomplete: item.value }
                    )
                  )
                }
              }}
            />
          ))}
        </div>
      ) : null}

      <div style={{ borderLeft: "1px solid #8C8C8C" }} className="flex pl-3 ml-3">
        <RisksToggleAll />
      </div>
    </div>
  )
}
